import { useCallback, useMemo, useState } from 'react';
import { BellOff, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MUTE_BUTTON_TOOLTIP,
  SUPPORT_READONLY_TOOLTIP,
} from '@/components/alertActionTooltipCopy';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { endOfDayUTC } from '@/lib/date-utils';
import {
  DURATION_DROPDOWN_LABELS,
  type DurationPreset,
  tomorrowUtcStart,
  untilDays,
  untilOneMonth,
} from '@/lib/durationPresets';
import { trackEvent } from '@/lib/analytics';
import { useMuteAlert, type AlertMuteScope } from '@/hooks/api/useAlertMutes';

/**
 * Shared min-width for the Mute/Unmute pair so toggling does not shift
 * layout. Imported by {@link AlertUnmuteButton}; aligned with the actions
 * column width in the org/user columns factories.
 */
export const MUTE_BUTTON_WIDTH_CLASS = 'min-w-[6.25rem] justify-center';

/**
 * Props for {@link AlertMuteButton}.
 */
export interface AlertMuteButtonProps {
  /** Scope of the alert (org-shared or self/account). */
  scope: AlertMuteScope;
  /** The alert key to mute. */
  alertKey: string;
  /**
   * Optional className passed to the trigger Button so callers can place the
   * control in tables, toolbars, or detail headers without overriding styles.
   */
  className?: string;
  /** Optional callback invoked after a successful mute. */
  onMuted?: () => void;
  /**
   * Optional analytics dimensions threaded into the `alert_muted` Mixpanel
   * event. Kept loose so callers (table cells, detail headers) can pass row
   * fields without coupling the button to the full `GroupedAlert` shape.
   */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
}

/**
 * Mute control for a grouped alert.
 *
 * Renders a dropdown with quick-pick durations (Forever, 1 day, 1 week,
 * 1 month) plus a "Custom..." option that opens a calendar dialog. The
 * mutation is performed by {@link useMuteAlert}, which handles optimistic
 * updates, rollback, and cache invalidation.
 *
 * Gates:
 * - **Feature flag** (`alert_muting`): if disabled or still loading, the
 *   component renders nothing. This is the primary safety net so the existing
 *   FE behaves identically when the flag is off.
 * - **Role** (org-scope only): hidden for `member` role; visible for
 *   `owner`/`manager`. Account-scope renders for any authenticated user
 *   (members can mute their own alerts).
 */
export function AlertMuteButton({
  scope,
  alertKey,
  className,
  onMuted,
  analyticsContext,
}: AlertMuteButtonProps): React.ReactElement | null {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_MUTING,
  ]);
  const mutingEnabled = flags[flagsmithFeatureFlags.ALERT_MUTING] === true;

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);

  // Centralizes "dialog dismissed" cleanup so every dismiss path (Cancel
  // button, Esc, backdrop click, programmatic close after submit) clears the
  // selected date. Without this, a date picked then dismissed via Esc would
  // persist and prefill the calendar on the next open.
  const handleCustomOpenChange = useCallback((open: boolean): void => {
    setCustomOpen(open);
    if (!open) setCustomDate(undefined);
  }, []);

  // Memoize the calendar's lower bound so we don't allocate a fresh `Date` +
  // matcher object on every render. react-day-picker compares the matcher
  // by reference, so reusing the same instance avoids superfluous work.
  const minDate = useMemo(() => tomorrowUtcStart(), []);

  const muteMutation = useMuteAlert(scope);
  const { toast } = useToast();

  // Wrap mutateAsync with the shared toast handler so success/failure produce
  // a consistent UX across every consumer (table row, detail header, etc.).
  // The hook still owns optimistic updates and rollback; this layer only
  // adds user-facing messaging.
  const handleMute = createMutationHandler(muteMutation, toast, {
    successMessage: 'Alert muted',
    errorMessage: 'Failed to mute alert',
  });

  if (flagsLoading || !mutingEnabled) {
    return null;
  }

  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const submitMute = async (
    mutedUntil: string | null,
    duration: DurationPreset
  ): Promise<void> => {
    try {
      await handleMute({ alertKey, mutedUntil });
      // Both side effects below run only on a confirmed mutation success.
      // They are deliberately ISOLATED from each other: a throw in one must
      // never block the other (telemetry/UX divergence is much worse than a
      // single skipped event). Order: callback first (user-observable), then
      // analytics (background).
      try {
        onMuted?.();
      } catch {
        // Caller-supplied callbacks are out of our control. Swallowing here
        // ensures a misbehaving consumer can't suppress the analytics event
        // for a mutation the backend has already accepted.
      }
      trackEvent('alert_muted', {
        scope: scope.kind,
        alert_key: alertKey,
        severity: analyticsContext?.severity,
        type: analyticsContext?.type,
        mute_duration: duration,
        muted_until: mutedUntil,
      });
    } catch {
      // createMutationHandler already toasted; swallow so the click handler
      // does not bubble an unhandled rejection up to React's error boundary.
    }
  };

  const handleCustomConfirm = async (): Promise<void> => {
    if (!customDate) return;
    const iso = endOfDayUTC(customDate);
    handleCustomOpenChange(false);
    await submitMute(iso, 'custom');
  };

  const isPending = muteMutation.isPending;

  return (
    <>
      <DropdownMenu>
        {/* Both Radix triggers `asChild` collapse to a single <button>; the
            tooltip dismisses on dropdown open because focus moves. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(MUTE_BUTTON_WIDTH_CLASS, className)}
                disabled={isPending || writeDisabled}
                aria-label="Mute alert"
              >
                {isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <BellOff className="size-4" />
                )}
                <span>Mute</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {writeDisabled ? SUPPORT_READONLY_TOOLTIP : MUTE_BUTTON_TOOLTIP}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => void submitMute(null, 'forever')}>
            {DURATION_DROPDOWN_LABELS.forever}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>For</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                onSelect={() => void submitMute(untilDays(1), '1_day')}
              >
                {DURATION_DROPDOWN_LABELS['1_day']}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => void submitMute(untilDays(7), '1_week')}
              >
                {DURATION_DROPDOWN_LABELS['1_week']}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => void submitMute(untilOneMonth(), '1_month')}
              >
                {DURATION_DROPDOWN_LABELS['1_month']}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setCustomOpen(true)}>
                {DURATION_DROPDOWN_LABELS.custom}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={handleCustomOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mute alert until…</DialogTitle>
            <DialogDescription>
              Pick the day this alert should be muted through. The mute expires
              at end-of-day UTC.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <Calendar
              mode="single"
              selected={customDate}
              onSelect={setCustomDate}
              disabled={{ before: minDate }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleCustomOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleCustomConfirm()}
              disabled={!customDate || isPending}
            >
              Mute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

AlertMuteButton.displayName = 'AlertMuteButton';
